// app/src/api/visitApi.ts

import { getAuthHeaders, handleApiResponse, queApi, criticalApi } from "@core/api/core"
import { createAsyncThunk } from "@reduxjs/toolkit"
import type { RootState } from "@core/store"
import { normalizeBetaSettingsData } from "@/types/hospitalSetting"
import {
  VisitListResponse,
  VisitListSerializer,
  VisitUpdateResponse,
  VisitWSerializer,
  VisitWResponse,
  VisitResponse,
  VisitStatusSerializer,
  VisitDailycountSerializer,
} from "@/types/visit"
import { jwtDecode } from "jwt-decode"
import { CommonTResponse } from "@/types/common"
import type { ReservationExtendSerializer } from "@/types/reservation"

export type PatientListName = "waiting" | "treatment" | "complete" | "reservation"
export const WAITING_ORDER_CHANGED_EVENT = "dashboard-waiting-order-changed"

export interface WaitingOrderResponse {
  visitDate: string
  visitUuids: string[]
}

export const getWaitingOrder = async (visitDate: string): Promise<WaitingOrderResponse> => {
  const response = await criticalApi.get("/visit/waiting-order/", { params: { visit_date: visitDate } })
  return handleApiResponse<WaitingOrderResponse>(response)
}

export const saveWaitingOrder = async (visitDate: string, visitUuids: string[]): Promise<WaitingOrderResponse> => {
  const response = await criticalApi.post("/visit/waiting-order/", { visitDate, visitUuids })
  return handleApiResponse<WaitingOrderResponse>(response)
}

export interface PatientListScope {
  list: PatientListName
  date: string
}
export interface PatientListSnapshot {
  snapshotId: string
  mode: "elements" | "full"
  elements: VisitElementsResponse["elements"]
  lists: (PatientListScope & { order?: string[]; rows?: VisitListSerializer[] | ReservationExtendSerializer[] })[]
}

export const getPatientListSnapshot = async (
  scopes: PatientListScope[],
  mode: "elements" | "full",
  visitUuids: string[]
): Promise<PatientListSnapshot> => {
  const response = await criticalApi.post("/visit/patient-list-snapshot-v1/", { scopes, mode, visitUuids: mode === "elements" ? visitUuids : [] })
  return handleApiResponse<PatientListSnapshot>(response)
}

/**
 * Redux Toolkit의 createAsyncThunk를 사용한 접수 생성 thunk.
 * @param params - { patientUuid, insuranceOrder } 정보를 포함하는 객체
 * @returns Promise<number>, 성공 시 생성된 접수번호를 반환, 실패 시 에러 메시지를 reject
 */

// DashboardList 목록 가져오기
export function visitFetchThunk(name: string) {
  return createAsyncThunk<VisitListSerializer[], string>(
    `visit/${name}`,
    async (visitDate: string, { getState }) => {
      const settings = normalizeBetaSettingsData((getState() as RootState).hospitalSetting?.settings?.betaSettings?.hospitalSettingData)
      if (settings.betaEnabled && settings.patientListIndividualRefreshEnabled) {
        return getVisitSnapshot(name, visitDate)
      }
      const response = await criticalApi.get(`/visit/${name}/`, {
        params: visitDate ? { visit_date: visitDate } : {},
      })
      return handleApiResponse<VisitListSerializer[]>(response)
    },
    {
      condition: (visitDate, { getState }) => {
        const selectedDate = (getState() as { selectedDate?: { selectedDate: string } }).selectedDate?.selectedDate
        return !selectedDate || !visitDate || selectedDate === visitDate
      },
    }
  )
}

export const getVisitSnapshot = async (visitStatus: string, visitDate: string): Promise<VisitListSerializer[]> => {
  const response = await criticalApi.get("/visit/snapshot-v1/", {
    params: { visit_status: visitStatus, ...(visitDate ? { visit_date: visitDate } : {}) },
  })
  return handleApiResponse<VisitListSerializer[]>(response)
}

export const getVisitSocketAuthTicket = async (socketSid: string, negotiationId: string): Promise<{ ticket: string }> => {
  const response = await criticalApi.post("/visit/socket-auth-v1/", { socketSid, negotiationId })
  return handleApiResponse<{ ticket: string }>(response)
}

export interface VisitElementsResponse {
  visitDate: string
  elements: { visitUuid: string; visit: VisitListSerializer | null }[]
  order: Record<"waiting" | "treatment" | "complete", string[]>
}

export const getVisitElements = async (visitUuids: string[], visitDate: string): Promise<VisitElementsResponse> => {
  const response = await criticalApi.get("/visit/elements-v1/", {
    params: { visit_uuids: visitUuids.join(","), visit_date: visitDate },
  })
  return handleApiResponse<VisitElementsResponse>(response)
}

// DashboardUpdate 이동하기
export const updateVisitData = async (data: VisitWSerializer): Promise<VisitStatusSerializer> => {
  const requestUrl = `/visit/${data.visitUuid}/`
  const response = await criticalApi.patch<VisitUpdateResponse>(requestUrl, data)
  // console.log("updateVisitData response:", response)
  return handleApiResponse<VisitStatusSerializer>(response)
}

export const updateVisitTreatmentDt = async (visitUuid: string, visitTreatmentDt: string): Promise<void> => {
  const requestUrl = `/visit/${visitUuid}/treatment-dt/`
  const response = await criticalApi.patch<CommonTResponse<{ visitTreatmentDt: string }>>(requestUrl, {
    visitTreatmentDt,
  })
  handleApiResponse<{ visitTreatmentDt: string }>(response)
}

export const updateVisitCompleteDt = async (visitUuid: string, visitCompleteDt: string): Promise<void> => {
  const requestUrl = `/visit/${visitUuid}/complete-dt/`
  const response = await criticalApi.patch<CommonTResponse<{ visitCompleteDt: string }>>(requestUrl, {
    visitCompleteDt,
  })
  handleApiResponse<{ visitCompleteDt: string }>(response)
}

export const updateVisitWaitingDt = async (visitUuid: string, visitWaitingDt: string): Promise<void> => {
  const requestUrl = `/visit/${visitUuid}/waiting-dt/`
  const response = await criticalApi.patch<CommonTResponse<{ visitWaitingDt: string }>>(requestUrl, {
    visitWaitingDt,
  })
  handleApiResponse<{ visitWaitingDt: string }>(response)
}

export const getVisitReceptionDt = async (visitUuid: string): Promise<string | null> => {
  const requestUrl = `/visit/${visitUuid}/reception-dt/`
  const response = await criticalApi.get<CommonTResponse<{ visitReceptionDt: string | null }>>(requestUrl)
  const data = handleApiResponse<{ visitReceptionDt: string | null }>(response)
  return data.visitReceptionDt || null
}

/////////////////////////////////////

export const createVisitApi = async (payload: VisitWSerializer): Promise<VisitWSerializer> => {
  console.log("createVisitApi 호출, payload:", payload)
  const requestUrl = `/visit/`
  const response = await criticalApi.post<VisitWResponse>(requestUrl, payload)
  return handleApiResponse<VisitWSerializer>(response)
}

export const getVisit = async (visitUuid: string): Promise<VisitListSerializer> => {
  const requestUrl = `/visit/${visitUuid}/`
  const response = await criticalApi.get<VisitResponse>(requestUrl)
  return handleApiResponse<VisitListSerializer>(response)
}

export const getVisitsByStatus = async (status: "waiting" | "treatment" | "complete", visitDate: string): Promise<VisitListSerializer[]> => {
  const requestUrl = `/visit/${status}/?visit_date=${visitDate}`
  const response = await criticalApi.get<VisitListResponse>(requestUrl)
  return handleApiResponse<VisitListSerializer[]>(response)
}

export const setVisitTreatmentStatus = async (visitUuid: string): Promise<void> => {
  const currentVisit = await getVisit(visitUuid)
  if (currentVisit.visitStatus !== "waiting") {
    return
  }

  await updateVisitData({
    visitUuid,
    visitStatus: "treatment",
    visitFunction: "next",
  })
}

/**
 * 환자의 가장 최근 Visit 정보를 복사하여 새로운 Visit 생성
 * visit_date는 자동으로 오늘 날짜로 설정됨
 * @param patientUuid - 환자 UUID
 * @param visitMemo - 접수 메모 (옵셔널)
 * @returns Promise<VisitWSerializer> - 생성된 Visit 정보
 */
export const createVisitFromRecent = async (
  patientUuid: string,
  visitMemo?: string,
  doctorUuid?: string,
  reservationUuid?: string
): Promise<VisitWSerializer> => {
  const requestUrl = `/visit/create-from-recent/`
  const params: { patientUuid: string; visitMemo?: string; doctorUuid?: string; reservationUuid?: string } = { patientUuid }
  if (visitMemo !== undefined && visitMemo !== "") {
    params.visitMemo = visitMemo
  }
  if (doctorUuid) {
    params.doctorUuid = doctorUuid
  }
  if (reservationUuid) {
    params.reservationUuid = reservationUuid
  }
  const response = await criticalApi.post<VisitWResponse>(requestUrl, params)
  return handleApiResponse<VisitWSerializer>(response)
}

// 기준일이 속한 월의 일별 방문 환자 수를 가져온다.
export const getVisitDailycount = async (referenceDate: string): Promise<VisitDailycountSerializer[]> => {
  const requestUrl = `/visit/dailycount/${referenceDate}/`
  const response = await criticalApi.get(requestUrl)
  return handleApiResponse<VisitDailycountSerializer[]>(response)
}
